import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { WarningCircle, ArrowsMerge } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchFilterBar, { filterRecords } from '@/components/SearchFilterBar';
import RequisitionDeleteButton from '@/components/RequisitionDeleteButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Th = ({ children, right }) => (
  <th className={`${right ? 'text-right' : 'text-left'} p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary`}>{children}</th>
);

const STATUS_LABEL = {
  pending_quotation: 'Pending Quotation',
  quotations_added: 'Quotations Added',
  vendor_approved: 'Vendor Approved',
  po_created: 'PO Created',
  po_released: 'PO Released',
  completed: 'Completed',
};

const DuplicateBadge = ({ dupes, onClick, testId }) => {
  if (!dupes || dupes.length === 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
      data-testid={testId}
      title="Other requisitions in the system share this catalogue number — review before approving"
    >
      <WarningCircle size={12} weight="fill" />
      {dupes.length} similar
    </button>
  );
};

export const ApprovalsPanel = ({ pendingApproval, awaitingQuotes, quotCounts, duplicateMap = {}, onReview, onProceed, onDeleted }) => {
  const { getAuthHeader } = useAuth();
  const [keyword, setKeyword] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dupContext, setDupContext] = useState(null); // { req, dupes }
  const [mergingId, setMergingId] = useState(null);

  const filteredApprovals = useMemo(
    () => filterRecords(pendingApproval, {
      keyword, startDate, endDate,
      fields: ['reagent_name', 'vendor_name', 'catalogue_number', 'employee_name', 'project_code_name'],
    }),
    [pendingApproval, keyword, startDate, endDate]
  );

  const openDupDialog = (req) => {
    setDupContext({ req, dupes: duplicateMap[req.id] || [] });
  };

  const canMergeStatus = (s) => s === 'pending_quotation' || s === 'quotations_added';

  const handleMerge = async (dup) => {
    if (!dupContext) return;
    const primary = dupContext.req;
    const newQty = (primary.quantity ?? 1) + (dup.quantity ?? 1);
    if (!window.confirm(
      `Merge "${dup.reagent_name}" (qty ${dup.quantity ?? 1}, by ${dup.employee_name}) INTO ` +
      `"${primary.reagent_name}" (qty ${primary.quantity ?? 1}, by ${primary.employee_name})?\n\n` +
      `→ New combined quantity: ${newQty}\n` +
      `→ ${dup.employee_name} will be added as a co-requester and still receive delivery updates.\n` +
      `→ The duplicate requisition will be deleted (audit-logged).`
    )) return;
    setMergingId(dup.id);
    try {
      await axios.post(
        `${API}/requisitions/${primary.id}/merge`,
        { duplicate_id: dup.id },
        { headers: getAuthHeader() }
      );
      toast.success(`Merged — combined quantity is now ${newQty}`);
      setDupContext(null);
      if (onDeleted) onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to merge requisitions');
    } finally {
      setMergingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <SearchFilterBar
        keyword={keyword} onKeywordChange={setKeyword}
        startDate={startDate} endDate={endDate}
        onStartDateChange={setStartDate} onEndDateChange={setEndDate}
        onClear={() => { setKeyword(''); setStartDate(''); setEndDate(''); }}
        placeholder="Search reagent, vendor, employee, project…"
        testIdPrefix="ops-approvals-filter"
      />
      <div className="bg-white border border-border-default rounded">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface border-b border-border-default">
              <tr>
                <Th>Reagent</Th><Th>Employee</Th><Th>Quotations</Th><Th right>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filteredApprovals.length === 0 && (
                <tr><td colSpan="4" className="p-8 text-center text-text-tertiary">No matching approvals.</td></tr>
              )}
              {filteredApprovals.map((req) => (
                <tr key={req.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`approval-req-${req.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-text-primary">
                        {req.reagent_name}
                        <span className="ml-1 text-xs text-text-tertiary">×{req.quantity ?? 1}</span>
                      </div>
                      <DuplicateBadge
                        dupes={duplicateMap[req.id]}
                        onClick={() => openDupDialog(req)}
                        testId={`duplicate-badge-${req.id}`}
                      />
                    </div>
                    <div className="text-sm text-text-tertiary">{req.catalogue_number}</div>
                  </td>
                  <td className="p-4 text-text-secondary">{req.employee_name}</td>
                  <td className="p-4 text-text-secondary">Ready for review</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        className="bg-accent-brand hover:bg-accent-brand-hover text-white"
                        onClick={() => onReview(req)}
                        data-testid={`review-btn-${req.id}`}
                      >
                        Review & Approve
                      </Button>
                      <RequisitionDeleteButton requisition={req} onDeleted={onDeleted} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Awaiting quotations — ops manager can override the 2-quote rule */}
      <div className="bg-white border border-border-default rounded">
        <div className="p-4 border-b border-border-default">
          <h3 className="font-semibold text-text-primary">Awaiting Quotations ({awaitingQuotes.length})</h3>
          <p className="text-sm text-text-tertiary">You decide when a requisition is ready — proceed with a single quotation if two aren&apos;t needed.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface border-b border-border-default">
              <tr>
                <Th>Reagent</Th><Th>Employee</Th><Th>Quotations</Th><Th right>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {awaitingQuotes.length === 0 && (
                <tr><td colSpan="4" className="p-8 text-center text-text-tertiary">No requisitions awaiting quotations.</td></tr>
              )}
              {awaitingQuotes.map((req) => {
                const count = quotCounts[req.id] || 0;
                return (
                  <tr key={req.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`awaiting-req-${req.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-text-primary">
                          {req.reagent_name}
                          <span className="ml-1 text-xs text-text-tertiary">×{req.quantity ?? 1}</span>
                        </div>
                        <DuplicateBadge
                          dupes={duplicateMap[req.id]}
                          onClick={() => openDupDialog(req)}
                          testId={`awaiting-duplicate-badge-${req.id}`}
                        />
                      </div>
                      <div className="text-sm text-text-tertiary">{req.catalogue_number}</div>
                    </td>
                    <td className="p-4 text-text-secondary">{req.employee_name}</td>
                    <td className="p-4">
                      <span className={`status-badge ${count > 0 ? 'status-info' : 'status-pending'}`}>
                        {count} quotation{count === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={count < 1}
                          onClick={() => onProceed(req.id)}
                          data-testid={`proceed-selection-btn-${req.id}`}
                          title={count < 1 ? 'At least one quotation is required' : 'Move to vendor selection now'}
                        >
                          Proceed to Selection
                        </Button>
                        <RequisitionDeleteButton requisition={req} onDeleted={onDeleted} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Similar requisitions dialog */}
      {dupContext && (
        <Dialog open={!!dupContext} onOpenChange={(o) => !o && setDupContext(null)}>
          <DialogContent className="sm:max-w-3xl" data-testid="duplicates-dialog">
            <DialogHeader>
              <DialogTitle>Similar requisitions in the system</DialogTitle>
              <DialogDescription>
                These share the same catalogue number as
                <strong className="text-text-primary"> {dupContext.req.reagent_name} </strong>
                (<span className="font-mono">{dupContext.req.catalogue_number}</span>).
                Approve the current requisition if it&apos;s a genuine repeat order, delete it if truly redundant,
                or <strong>merge</strong> a twin into it to roll up quantities in one click.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[420px] overflow-y-auto border border-border-default rounded">
              <table className="w-full text-sm">
                <thead className="bg-surface border-b border-border-default sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Reagent</th>
                    <th className="p-2 text-left">Employee</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Created</th>
                    <th className="p-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dupContext.dupes.map((d) => {
                    const canMerge = canMergeStatus(dupContext.req.status) && canMergeStatus(d.status);
                    return (
                      <tr key={d.id} className="border-b border-border-default" data-testid={`duplicate-row-${d.id}`}>
                        <td className="p-2">
                          <div className="font-medium text-text-primary">{d.reagent_name}</div>
                          <div className="text-xs text-text-tertiary font-mono">{d.catalogue_number}</div>
                        </td>
                        <td className="p-2 text-text-secondary">{d.employee_name}</td>
                        <td className="p-2 text-right">{d.quantity ?? 1}</td>
                        <td className="p-2">
                          <span className="status-badge status-info text-xs">{STATUS_LABEL[d.status] || d.status}</span>
                        </td>
                        <td className="p-2 text-text-secondary">
                          {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="p-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!canMerge || mergingId === d.id}
                            onClick={() => handleMerge(d)}
                            data-testid={`merge-duplicate-${d.id}`}
                            title={canMerge
                              ? `Combine into ${dupContext.req.reagent_name} (new qty ${(dupContext.req.quantity ?? 1) + (d.quantity ?? 1)})`
                              : 'Only pending / quotations-added requisitions can be merged'}
                          >
                            <ArrowsMerge size={14} className="mr-1" weight="bold" />
                            {mergingId === d.id ? 'Merging…' : 'Merge into this'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setDupContext(null)} data-testid="close-duplicates-dialog">
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ApprovalsPanel;
